import { FC, useMemo } from "react";

import { formatAssessmentCategory } from "./utils";

import { StudentOutcomeScoreTableData } from "api/api-types-assessment";
import { displayOutcomeName } from "components/outcomes/helpers";
import { OutcomeType } from "components/server-types";
import { ScrollableTableContainer } from "components/tables/ScrollableTableContainer";
import { TD, TH, THead, TR, Table } from "components/tables/TableElements";
import { t } from "i18n/i18n";
import { TooltipTrigger } from "mds/components/TooltipTrigger";
import { EyeNotVisibleIcon, ScoreIcon } from "mds/icons";
import { useSectionCapabilitiesBySectionId } from "store/capability-hooks";
import { Link } from "utils/routing";
import { pageUrl } from "utils/urls";

type StudentOutcomeScoreTableProps = {
  sectionId: string;
  tableData: StudentOutcomeScoreTableData;
};

export const StudentOutcomeScoreTable: FC<StudentOutcomeScoreTableProps> = ({
  sectionId,
  tableData,
}) => {
  const { canParticipateInSection } = useSectionCapabilitiesBySectionId(sectionId);
  const { section_outcomes: sectionOutcomes, pages } = tableData;

  const scoresByPageId = useMemo(
    () =>
      Object.fromEntries(
        pages.map((page) => [
          page.page_id,
          Object.fromEntries(
            page.outcome_scores.map((outcomeScore) => [
              outcomeScore.section_outcome_id,
              outcomeScore.score,
            ]),
          ),
        ]),
      ),
    [pages],
  );

  if (pages.length === 0) {
    return null;
  }

  return (
    <ScrollableTableContainer title={t("assessment_dashboard.student_outcome_index_title")}>
      <Table>
        {/* Headers: Page / Page Type/ Outcome A / Outcome B / Outcome C */}
        {/* One row per student, showing each student's aggregate outcome scores */}
        <THead>
          <TR>
            <TH className="w-4/12 pl-4">{t("assessment_dashboard.all.page_header_title")}</TH>

            <TH className="w-2/12">
              <TooltipTrigger
                content={t("assessment_dashboard.all.assessment_category_header_tooltip")}
              >
                {t("assessment_dashboard.all.assessment_category_header_title")}
              </TooltipTrigger>
            </TH>

            {sectionOutcomes.map((sectionOutcome) => {
              const outcomeTitle = displayOutcomeName({
                id: sectionOutcome.section_outcome_id,
                title: sectionOutcome.title,
              } as OutcomeType);

              return (
                <TH
                  key={sectionOutcome.section_outcome_id}
                  verticalDivClassName="max-h-[180px]"
                  verticalDivTitle={outcomeTitle}
                  isVertical
                >
                  {outcomeTitle}
                </TH>
              );
            })}
          </TR>
        </THead>
        <tbody>
          {pages.map((row) => (
            <TR key={row.page_id}>
              <TD className="min-w-0 max-w-24 truncate py-2.5 pl-[17px]">
                <div className="body-xs flex w-full items-center gap-1">
                  {row.hidden && (
                    <TooltipTrigger content={t("assessment_dashboard.all.hidden_page_tooltip")}>
                      <EyeNotVisibleIcon className="inline shrink-0 text-black-tint-40" />
                    </TooltipTrigger>
                  )}
                  {row.hidden && canParticipateInSection ? (
                    <span className="truncate">{row.title}</span>
                  ) : (
                    <Link
                      className="min-w-0 truncate text-blue-shade-20 underline"
                      to={pageUrl(sectionId, row.page_id)}
                    >
                      {row.title}
                    </Link>
                  )}
                </div>
              </TD>

              <TD>{formatAssessmentCategory(row.assessment_category)}</TD>

              {sectionOutcomes.map((sectionOutcome) => {
                const score = scoresByPageId[row.page_id]?.[sectionOutcome.section_outcome_id];

                return (
                  <TD key={sectionOutcome.section_outcome_id}>
                    <ScoreIcon value={score ?? NaN} />
                  </TD>
                );
              })}
            </TR>
          ))}
        </tbody>
      </Table>
    </ScrollableTableContainer>
  );
};
